import * as Sentry from '@sentry/nextjs';
import { Dispatch, RefObject, SetStateAction, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import StickyBarLinks from '@features/productPreview/sections/StickyBar/components/StickyBarLinks/StickyBarLinks';
import AskProductButton from '@features/productPreview/sections/StickyBar/components/AskProductButton/AskProductButton';
import styles from '@features/productPreview/sections/StickyBar/StickyBar.styles';
import { uiState } from '@features/productPreview/mobX/state/context';
import ErrorBoundary from '@components/errors/ErrorBoundary';
import theme from '@styles/theme';
import Icon from '@components/icons/Icon';
import Button from '@components/Button/Button';
import IsShopMiddleware from '@utils/IsShopMiddleware';
import IsCustomerMiddleware from '@utils/IsCustomerMiddleware';
import layoutUiState from '@features/layout/mobX/LayoutUiState';
import IsGenerallyVisible from '@utils/IsGenerallyVisible';
type PropTypes = {
  stickyBarLinks: Array<string>;
  isDesktop: boolean;
  configuratorRef: RefObject<HTMLElement | null>;
  setProductPrice: Dispatch<SetStateAction<string>>;
  isAddToCartDisabled: boolean;
  handleConfiguratorLoading: Dispatch<SetStateAction<boolean>>;
};
const StickyBar = ({
  stickyBarLinks,
  isDesktop,
  configuratorRef,
  setProductPrice,
  isAddToCartDisabled,
  handleConfiguratorLoading
}: PropTypes) => {
  const {
    asPath
  } = useRouter();
  const {
    selectedSlider,
    selectedTechSlider,
    setProductName,
    productName
  } = uiState;
  const [isTriggered, setIsTriggered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [price, setPrice] = useState<string>('');
  const {
    t
  } = useTranslation('common');
  const [showAddtoCartButton, setshowAddtoCartButton] = useState(false);
  const [showAskProductButton, setShowAskProductButton] = useState(false);
  const [hasChat, setHasChat] = useState(false);
  const [layoutBarsTotalHeight, setLayoutBarsTotalHeight] = useState(0);
  let timer: NodeJS.Timeout;
  const {
    getLayoutFixedBarsHeight
  } = layoutUiState;
  const buttonElementIsActive = (button: Element | false): boolean => {
    if (button) {
      const {
        disabled
      } = button as HTMLButtonElement;
      return IsGenerallyVisible(button) && !disabled;
    }
    return false;
  };
  const getConfiguratorButtonAddToCart = (): Element | false => {
    const basicAddToCart = document.getElementsByClassName('vpw-cart__add-card')[0];
    const simpleAddToCart = document.getElementById('zatwierdz');
    const creativeAddToCart = document.getElementsByClassName('add-to-cart')[0];
    const frontConfiguratorAddToCart = document.getElementsByClassName('fc_add_to_cart')[0];
    return basicAddToCart || simpleAddToCart || creativeAddToCart || frontConfiguratorAddToCart;
  };
  const getConfiguratorButtonAskInChat = (): Element | false => {
    const basicAddToCart = document.getElementsByClassName('no__shop__chat')[0];
    const frontConfiguratorAddToCart = document.getElementsByClassName('fc_see_in_voxbox')[0];
    return basicAddToCart || frontConfiguratorAddToCart;
  };
  const getConfiguratorButtonSeeInShowroom = (): Element | false => {
    const basicAddToCart = document.getElementsByClassName('js-card-showroom-click')[0];
    const frontConfiguratorAddToCart = document.getElementsByClassName('fc_ask_question')[0];
    return basicAddToCart || frontConfiguratorAddToCart;
  };
  const handleButtons = () => {
    if (configuratorRef.current !== null) {
      const configurator = document.getElementById('configurator') || document.getElementById('configurator-front');
      if (!configurator) {
        return;
      }
      const isConfiguratorLoaded = document.querySelector('.vpw-cart__add-card') || document.getElementById('zatwierdz') || document.querySelector('.add-to-cart') || document.querySelector('.fc_add_to_cart');
      if (!isConfiguratorLoaded) {
        setTimeout(handleButtons, 500);
        return;
      }
      const buttonAddToCartNode = getConfiguratorButtonAddToCart();
      if (buttonElementIsActive(buttonAddToCartNode)) {
        setshowAddtoCartButton(true);
        setShowAskProductButton(false);
        handleConfiguratorLoading(false);
      } else {
        setshowAddtoCartButton(false);
        const buttonAskInChatNode = getConfiguratorButtonAskInChat();
        const buttonSeeInShowroomNode = getConfiguratorButtonSeeInShowroom();
        if (buttonElementIsActive(buttonAskInChatNode) || buttonElementIsActive(buttonSeeInShowroomNode)) {
          setHasChat(!!buttonAskInChatNode && buttonElementIsActive(buttonAskInChatNode));
          setShowAskProductButton(true);
        } else {
          setShowAskProductButton(false);
        }
      }
    }
  };
  const handleStickyBar = () => {
    if (configuratorRef.current !== null) {
      const configurator = document.getElementById('configurator') || document.getElementById('configurator-front');
      if (!configurator) {
        return;
      }
      const heightVh = configuratorRef.current.offsetHeight;
      if (window.scrollY > heightVh) {
        setIsVisible(true);
        setIsTriggered(true);
        handleButtons();

        // Try to get the price again when StickyBar is visible
        const frontPriceElement = document.querySelector('.fc_front_price');
        if (frontPriceElement?.textContent) {
          setPrice(frontPriceElement.textContent);
          setProductPrice(frontPriceElement.textContent);
        }
      } else {
        setIsVisible(false);
      }
      setLayoutBarsTotalHeight(getLayoutFixedBarsHeight());
    } else {
      window.removeEventListener('scroll', handleStickyBar);
      clearTimeout(timer);
    }
  };
  const handleOnClickAskProductInChat = () => {
    const buttonAskInChatNode = getConfiguratorButtonAskInChat();
    if (buttonElementIsActive(buttonAskInChatNode)) {
      (buttonAskInChatNode as HTMLButtonElement).click();
    }
  };
  useEffect(() => {
    window.addEventListener('scroll', handleStickyBar);
    const initButtonsTimer = setTimeout(() => {
      handleButtons();
    }, 1000);
    timer = setInterval(() => handleStickyBar(), 100);
    return () => {
      window.removeEventListener('scroll', handleStickyBar);
      clearTimeout(timer);
      clearTimeout(initButtonsTimer);
    };
  }, [configuratorRef]);
  useEffect(() => {
    setPrice('');
    setshowAddtoCartButton(false);
    setShowAskProductButton(false);
    const targetNode = document.getElementById('configurator') || document.getElementById('configurator-front');
    if (targetNode === null) {
      const canNotFindProductConfigurator: Sentry.Event = {
        message: 'Configurator not loaded on product page!',
        level: 'error',
        breadcrumbs: [{
          message: "Can not find '#configurator' element",
          level: 'error'
        }]
      };
      Sentry.captureEvent(canNotFindProductConfigurator);
      return;
    }
    let configuratorObserver: MutationObserver | null = null;
    const config = {
      attributes: true,
      childList: true,
      subtree: true
    };
    const cleanSimplePrice = (mutation: Element): string => {
      if (!mutation.hasChildNodes() && mutation.textContent) return mutation.textContent;
      if (mutation.lastChild?.textContent) return mutation.lastChild?.textContent;
      return '';
    };
    const isConfiguratorFullyLoaded = () => {
      const frontPriceElement = document.querySelector('.fc_front_price');
      return !!frontPriceElement;
    };
    const updateProductNameFromConfigurator = () => {
      const configuratorNodes: NodeListOf<Element>[] = [document.querySelectorAll('.vpw-header__product-name'), document.querySelectorAll('.st-main-menu-header-name-bottom'), document.querySelectorAll('.vt-menu-main-header'), document.querySelectorAll('.fc_name')];
      const productNameNode = configuratorNodes.find(node => node.length > 0);
      if (productNameNode?.[0]?.textContent) {
        setProductName(productNameNode[0].textContent);
      }
    };
    const tryGetPrice = () => {
      if (!isConfiguratorFullyLoaded()) {
        setTimeout(tryGetPrice, 500);
        return;
      }
      const frontPriceElement = document.querySelector('.fc_front_price');
      if (frontPriceElement?.textContent) {
        setPrice(frontPriceElement.textContent);
        setProductPrice(frontPriceElement.textContent);
      }
      updateProductNameFromConfigurator();
    };
    setTimeout(tryGetPrice, 2000);
    const callback = (mutationsList: MutationRecord[]) => {
      const isFrontConfigurator = document.querySelector('.fc_name');
      mutationsList.forEach(mutation => {
        if (mutation.target instanceof HTMLElement) {
          const isBasicConfigurator = mutation.type === 'childList' && mutation.target.classList.contains('vpw-cart__price-value');
          const isSimpleConfigurator = mutation.type === 'childList' && mutation.target.classList.contains('st-main-menu-price-value');
          const isCreativeConfigurator = Array.from(mutation.target.childNodes).filter(node => node instanceof HTMLElement && node.classList.contains('v-application'));
          if (isBasicConfigurator || isSimpleConfigurator || isCreativeConfigurator) {
            const creativePriceNode = isCreativeConfigurator && document.querySelectorAll('.price-container').length > 0 ? document.querySelectorAll('.price-container')[0] : null;
            const priceElement = isBasicConfigurator || isSimpleConfigurator ? mutation.target : creativePriceNode;
            if (priceElement?.textContent) {
              const isSimplePrice = priceElement.classList.contains('st-main-menu-price-value');
              setPrice(isSimplePrice ? cleanSimplePrice(priceElement) : cleanSimplePrice(priceElement));
              setProductPrice(isSimplePrice ? cleanSimplePrice(priceElement) : cleanSimplePrice(priceElement));
            }
            if (isFrontConfigurator?.textContent) {
              const priceNode = document.querySelector('.fc_front_price');
              setPrice(priceNode?.textContent || '');
              setProductPrice(priceNode?.textContent || '');
            }
            updateProductNameFromConfigurator();
          }
        }
      });
    };
    try {
      configuratorObserver = new MutationObserver(callback);
      configuratorObserver.observe(targetNode, config);
    } catch (error) {
      Sentry.captureException(error);
    }
    return function cleanup() {
      configuratorObserver?.disconnect();
    };
  }, [asPath]);
  const handleScrollUp = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleAddToCart = () => {
    const buttonNode = getConfiguratorButtonAddToCart();
    if (buttonNode) {
      const event = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: false
      });
      buttonNode.dispatchEvent(event);
    } else {
      const canNotAddProductToCartEvent: Sentry.Event = {
        message: 'User can not add product from sticky bar to cart!',
        level: 'error',
        breadcrumbs: [{
          message: "Can not find button '#vpw-cart__add-card' in product configurator",
          level: 'error'
        }]
      };
      Sentry.captureEvent(canNotAddProductToCartEvent);
    }
  };
  if (!isTriggered) {
    return <></>;
  }
  return <ErrorBoundary>
      <nav sx={styles.stickyNav(isVisible && !selectedSlider && !selectedTechSlider, layoutBarsTotalHeight)} data-cy="StickyBar">
        <div sx={styles.stickyLeftBox}>
          <Button variant="iconOnly" onClick={handleScrollUp} aria-label={t('scrollUp')}>
            <Icon icon="ArrowBack" color={theme.rawColors.darkGrey700} size="33.33px" cy="ArrowBack" />
          </Button>
          {isDesktop && <div sx={styles.productTitle}>
              <span>{productName}</span>
            </div>}
        </div>
        <StickyBarLinks stickyBarLinks={stickyBarLinks} isDesktop={isDesktop} />
        <IsShopMiddleware>
          {!showAddtoCartButton && showAskProductButton && isDesktop && <AskProductButton hasFlagChat={hasChat} handleOnClickAskProductInChat={handleOnClickAskProductInChat} />}
          {showAddtoCartButton && price && <div sx={styles.stickyCart}>
              <div sx={styles.stickyAddToCart}>
                <div sx={styles.stickyPriceBox} data-cy="StickyBarPriceBox">
                  {price}
                </div>
                <IsCustomerMiddleware>
                  <Button variant="default" variantColor="dark" disabled={isAddToCartDisabled} addSx={styles.addToCartButton} onClick={handleAddToCart} cy="StickyBarAddToCartButton">
                    {t('add_to_cart')}
                    <Icon icon="ShoppingCart" color={theme.rawColors.neutralWhite} size="23px" margin="0 0 0 6px" />
                  </Button>
                </IsCustomerMiddleware>
              </div>
            </div>}
        </IsShopMiddleware>
      </nav>
    </ErrorBoundary>;
};
export default observer(StickyBar);